import { DevLogger } from '~/utils/DevLogger';

export interface ISaltclickPendingChatEnd {
  /** What the widget was holding when it ended; empty when the net could not say. */
  conversationIds: string[];
  /** When the reply window closes, or `null` while the chat is back on screen. */
  deadline: number | null;
}

const keyFor = (accountId: string) => `saltclickPendingChatEnd-${accountId}`;

/**
 * The chat end still waiting out its reply window, kept across page loads: in memory it
 * only lived as long as the document, so a tab closed or reloaded inside the window left
 * the conversation open on Salesforce with nothing left to ever close it. Mirrors
 * mobile's `SaltclickChatEndStorage`, with `localStorage` in place of MMKV.
 *
 * `null` when there is none or the value is unusable — a record we cannot read says
 * nothing about the conversation, and acting on one would retire a chat somebody is still
 * in.
 */
export const getSaltclickPendingChatEnd = (accountId: string): ISaltclickPendingChatEnd | null => {
  let stored: Partial<ISaltclickPendingChatEnd> | null;
  try {
    stored = JSON.parse(localStorage.getItem(keyFor(accountId)) ?? 'null');
  } catch {
    return null;
  }

  if (
    !stored ||
    typeof stored !== 'object' ||
    !Array.isArray(stored.conversationIds) ||
    !stored.conversationIds.every((id) => typeof id === 'string') ||
    (stored.deadline !== null && !Number.isFinite(stored.deadline))
  ) {
    return null;
  }

  return { conversationIds: stored.conversationIds, deadline: stored.deadline ?? null };
};

// Both writes swallow storage errors (private mode, quota): losing the record costs the
// retry on the next load, never the chat in front of the user.
export const setSaltclickPendingChatEnd = (
  accountId: string,
  pendingEnd: ISaltclickPendingChatEnd,
): void => {
  try {
    localStorage.setItem(keyFor(accountId), JSON.stringify(pendingEnd));
  } catch (err) {
    DevLogger.error('[Saltclick] Could not store the pending chat end: ', err);
  }
};

export const clearSaltclickPendingChatEnd = (accountId: string): void => {
  try {
    localStorage.removeItem(keyFor(accountId));
  } catch (err) {
    DevLogger.error('[Saltclick] Could not clear the pending chat end: ', err);
  }
};
